/**
 * Inheritance of Scene with a WebGL version
 * @class WebGLScene
 * @extends Scene
 * @param {HTMLCanvasElement} element
 * @param {Object} uris
 * @param {Array.<WebGLLayer>} layers
 * @param {Object} [contextOptions]
 * @constructor
 */
function WebGLScene(element, uris, layers, contextOptions) {
	Scene.call(this, element);
	contextOptions = contextOptions || {alpha: false};
	//TODO do more tests
	var context = this._context = element.getContext("webgl", contextOptions) || element.getContext("experimental-webgl", contextOptions);
	this._s3tcExtension = context.getExtension("WEBGL_compressed_texture_s3tc") || context.getExtension("MOZ_WEBGL_compressed_texture_s3tc") || context.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc");
	this._imagesLoadHandlerBound = this._imagesLoadHandler.bind(this);
	this._tarProgressHandlerBound = this._tarProgressHandler.bind(this);
	this._tarErrorHandlerBound = this._tarErrorHandler.bind(this);
	this._tarAbortHandlerBound = this._tarAbortHandler.bind(this);
	this._tarLoadHandlerBound = this._tarLoadHandler.bind(this);

	this._layers = layers || [];
	// Update texture size
	var textureSize = context.getParameter(context.MAX_TEXTURE_SIZE) >= 4096 ? 4096 : 2048;
	for(var layerIndex = 0, numLayers; layerIndex < numLayers; layerIndex++){
		layers[layerIndex].textureSize = textureSize;
	}
	this._tarURI = uris["src" + textureSize];
	this._tarSize = parseInt(uris["size" + textureSize]);
	this._init();
}

//Inheritance
WebGLScene.prototype = Object.create(Scene.prototype);
WebGLScene.prototype.constructor = WebGLScene;

/** @type Array.<WebGLLayer> */
WebGLScene.prototype._layers = null;
/** @type WebGLShader */
WebGLScene.prototype._vertexShader = null;
/** @type WebGLShader */
WebGLScene.prototype._fragmentShader = null;
/** @type WebGLProgram */
WebGLScene.prototype._program = null;
/** @type WebGLUniformLocation */
WebGLScene.prototype._positionLocationAttr = null;
/** @type WebGLUniformLocation */
WebGLScene.prototype._texCoordLocationAttr = null;
/** @type WebGLUniformLocation */
WebGLScene.prototype._resolutionLocationAttr = null;
/** @type Function */
WebGLScene.prototype._imagesLoadHandlerBound = null;
/** @type Number */
SimpleScene.prototype._numImagesLoaded = 0;
/** @type Number */
SimpleScene.prototype._numImages = 0;
/** @type WebGLRenderingContext */
SimpleScene.prototype._context = 0;
/** @type Object */
SimpleScene.prototype._s3tcExtension = null;
/**
 * Will store relation between TARReaderFile and image.src (of it's content)
 * @type {Object}
 */
SimpleScene.prototype._imagesFileBySrc = null;
/** @type String */
SimpleScene.prototype._tarURI = null;
/** @type Number */
SimpleScene.prototype._tarSize = null;
/** @type XMLHttpRequest */
SimpleScene.prototype._tarLoader = null;
SimpleScene.prototype._tarLoadAsyncID = 0;
SimpleScene.prototype._tarProgressHandlerBound = null;
SimpleScene.prototype._tarErrorHandlerBound = null;
SimpleScene.prototype._tarAbortHandlerBound = null;
SimpleScene.prototype._tarLoadHandlerBound = null;

/**
 * Resize scene
 * @param width
 * @param height
 */
WebGLScene.prototype.updateSize = function updateSize(width, height) {
	this._context.viewport(0, 0, /*this._context.drawingBufferWidth*/width, /*this._context.drawingBufferHeight*/height);

	// set the resolution
	this._context.uniform2f(this._resolutionLocationAttr, width, height);
//	console.log(this._context.getUniform(this._program, this._resolutionLocationAttr), [this._context._canvas.clientWidth, this._context._canvas.clientHeight]);
};

WebGLScene.prototype._tarProgressHandler = function _tarProgressHandler(event) {
	var total = event.total;
	// Not computable
	// WB36
	if(!event.lengthComputable){
		total = isNaN(this._tarSize) ? 0 : this._tarSize;
	}
	this._dispatchProgressEvent(event.loaded, total);

};
WebGLScene.prototype._tarErrorHandler = function _tarErrorHandler() {
	this._dispatchErrorEvent();
};
WebGLScene.prototype._tarAbortHandler = function _tarAbortHandler() {
	this._dispatchAbortEvent();
};
WebGLScene.prototype._tarLoadHandler = function _tarLoadHandler(event) {
	// TODO use worker instead
//	this._tarLoadAsync();
	this._tarLoadAsyncID = setTimeout(this._tarLoadAsync.bind(this), 0);
};
WebGLScene.prototype._tarLoadAsync = function _tarLoadAsync() {
	var tarReader = new TarReader();
	tarReader.readChunk(this._tarLoader.response);
	this._readTar(tarReader);
};

/**
 * Read each file in TAR (DDS, JSON, PNG, JPEG, etc.)
 * @param {TarReader} tarReader
 * @private
 * @see _readDDS()
 * @see _readJSON()
 * @see _readImage()
 */
WebGLScene.prototype._readTar = function _readTar(tarReader) {
	var file;
	var mimetype;
	var mimetypes = [];
	var imagesFile = [];
	var fileIndex;
	var numFiles;

	for(fileIndex = 0, numFiles = tarReader.files.length; fileIndex < numFiles; fileIndex++) {
		file = tarReader.files[fileIndex];
		mimetype = null;
		// We don't have any other metadata than file extension
		switch(this._getFilenameExtension(file.name).toLowerCase()) {
			// DDS
			case "dds":
				this._readDDS(file);
				break;
			// JSON
			case "json":
				this._readJSON(file);
				break;
			// Image JPEG or PNG
			case "png":
				mimetype = mimetype ? mimetype : "image/png";
			case "jpg":
			case "jpeg":
				mimetype = mimetype ? mimetype : "image/jpeg";
				// Since for images, we do async operations, we need first the number of images
				// In a queue we store
				// the mimetype
				mimetypes.push(mimetype);
				// and file entry
				this._numImages = imagesFile.push(file);
				break;
			default:
				console.log("Tar: Unknown file \"" + file.name + "\"");
		}
	}

	// We have images to load
	if(this._numImages > 0){
		this._numImagesLoaded = 0;
		for(fileIndex = 0, numFiles = imagesFile.length; fileIndex < numFiles; fileIndex++) {
			this._readImage(imagesFile[fileIndex], mimetypes[fileIndex]);
		}
	}
	// No images
	else{
		this._loaded = true;
		this._dispatchLoadEvent();
	}
};

/**
 * Find a layer by it's name
 * @param {String} name
 * @returns {WebGLLayer|null}
 * @private
 */
WebGLScene.prototype._findLayerByName = function _findLayerByName(name){
	for(var index = 0, numLayers = this._layers.length; index < numLayers; index++){
		if(this._layers[index].name == name){
			return this._layers[index];
		}
	}

	return null;
};

/**
 * Find appropriate layer and index then set given texture
 * @param {String} id
 * @param {WebGLTexture} texture
 * @private
 */
WebGLScene.prototype._setTexture = function _setTexture(id, texture){
	// Extract index part
	var indexStr = id.match(/\d+$/);
	var index = parseInt(indexStr, 10);
	if(isNaN(index)){
		indexStr = "";
		index = 0;
	}
	var layerName;
	var layer;
	if(
		// Get layer name and test its validity
		(layerName = id.substr(0, id.length - indexStr.length))
		// Get layer and test if exist
		&& (layer = this._findLayerByName(layerName))
	){
		layer.textures[index] = texture;
	}
};

WebGLScene.prototype._readDDS = function _readDDS(file){
	var texture;
	var ddsReader = new DDSReader();
	try{
		ddsReader.read(file.data);
	}catch(error){
		return;
	}

	if(
		// Has mipmaps
		ddsReader.mipmaps.length > 0
		// And first one is not null
		&& ddsReader.mipmaps[0]
		// And a texture created
		&& (texture = this._createTexture(ddsReader.mipmaps[0]))
	){
		this._setTexture(this._getFilename(file.name), texture);
	}
};

WebGLScene.prototype._readJSON = function _readJSON(file){
	var byteLength = file.data.byteLength;
	var strData = "";
	var data = new Uint8Array(file.data);
	var layer;
	// Create a string from bytes
	for(var byteIndex = 0; byteIndex < byteLength; ++byteIndex){
		strData += String.fromCharCode(data[byteIndex]);
	}

	// Try to parse JSON
	try{
		data = JSON.parse(strData);
	}catch(error){

	}

	// Set frames data for the appropriate layer
	if(data && (layer = this._findLayerByName(this._getFilename(file.name)))){
		layer.frames = data;
	}
};

WebGLScene.prototype._readImage = function _readImage(file, mimetype){
	// Will store relation between file and image.src
	if(!this._imagesFileBySrc){
		this._imagesFileBySrc = {};
	}

	var blob = new Blob([new Uint8Array(file.data)], {type: mimetype});

	var image = document.createElement("img");
	image.addEventListener("load", this._imagesLoadHandlerBound, false);
	//TODO handle error too
	var src = URL.createObjectURL(blob);
	this._imagesFileBySrc[src] = file;
	// Will load the image (sync or async)
	image.src = src;
};

WebGLScene.prototype._imagesLoadHandler = function _imagesLoadHandler(event){
	var image = event.currentTarget;
	image.removeEventListener("load", this._imagesLoadHandlerBound, false);
	var src = image.src;
	// Destroy Blob and it's URL
	// (a WebGL can't access anymore to pixels)
	window.URL.revokeObjectURL(src);
	var file = this._imagesFileBySrc[src];
	var texture;
	if(
		// Test image validity
		image.naturalWidth > 0 && image.naturalHeight > 0
		// And a texture created
		&& (texture = this._createTexture(image))
	){
		this._setTexture(this._getFilename(file.name), texture);
	}

	if(++this._numImagesLoaded >= this._numImages) {
		this._loaded = true;
		this._imagesFileBySrc = null;
		this._dispatchLoadEvent();
	}
};

/**
 * Get filename extension like `.png` or `.txt
 * @param {String} file
 * @return {String|null}
 * @private
 */
WebGLScene.prototype._getFilenameExtension = function _getFileExtension(file){
	var dotIndex = file.lastIndexOf(".");
	if(dotIndex < 0){
		return null;
	}

	return file.substr(dotIndex + 1);
};

/**
 * Get filename like `script` for `script.js`
 * @param {String} file
 * @returns {String}
 * @private
 */
WebGLScene.prototype._getFilename = function _getFileExtension(file){
	var dotIndex = file.lastIndexOf(".");
	if(dotIndex < 0){
		return file;
	}

	return file.substr(0, dotIndex);
};

WebGLScene.VERTEX_SHADER_SRC = "attribute vec2 a_position;\nattribute vec2 a_texCoord;\nuniform vec2 u_resolution;\nvarying vec2 v_texCoord;\n\nvoid main() {\n\t// convert the rectangle from pixels to 0.0 to 1.0\n\tvec2 zeroToOne = a_position / u_resolution;\n\t// convert from 0->1 to 0->2\n\tvec2 zeroToTwo = zeroToOne * 2.0;\n\t// convert from 0->2 to -1->+1 (clipSpace)\n\tvec2 clipSpace = zeroToTwo - 1.0;\n\tgl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);\n\t// pass the texCoord to the fragment shader\n\t// The GPU will interpolate this value between points.\n\tv_texCoord = a_texCoord;\n}";
WebGLScene.FRAGMENT_SHADER_SRC = "precision mediump float;\nuniform sampler2D u_canvas1;\nuniform float u_contrast;\nuniform float u_brightness;\nvarying vec2 v_texCoord;\n\nvoid main() {\n\tvec4 fragColor = texture2D(u_canvas1, v_texCoord);\n\tvec3 color = fragColor.rgb;\n\t//Set Brightness\n\tfloat bFactor = 1.0 - abs(u_brightness) * 0.5;\n\tcolor *= bFactor;\n\tcolor += u_brightness > 0. ? 1.0 - bFactor: 0.0;\n\t//Set Contrast\n\tcolor = (color - 0.5) * (1.0 + u_contrast) + 0.5;\n\tgl_FragColor = vec4(color, fragColor.a);\n}";

WebGLScene.prototype._init = function _init() {
	// Create the shader object
	this._vertexShader = this._context.createShader(this._context.VERTEX_SHADER);
	// Load the shader source
	this._context.shaderSource(this._vertexShader, WebGLScene.VERTEX_SHADER_SRC);
	// Compile the shader
	this._context.compileShader(this._vertexShader);
//	console.log(this._context.getShaderParameter(this._vertexShader, this._context.COMPILE_STATUS));
//	console.log(this._context.getShaderInfoLog(this._vertexShader));

	// Create the shader object
	this._fragmentShader = this._context.createShader(this._context.FRAGMENT_SHADER);
	// Load the shader source
	this._context.shaderSource(this._fragmentShader, WebGLScene.FRAGMENT_SHADER_SRC);
	// Compile the shader
	this._context.compileShader(this._fragmentShader);
//	console.log(this._context.getShaderParameter(this._fragmentShader, this._context.COMPILE_STATUS));
//	console.log(this._context.getShaderInfoLog(this._fragmentShader));

	//Program
	this._program = this._context.createProgram();
	this._context.attachShader(this._program, this._vertexShader);
	this._context.attachShader(this._program, this._fragmentShader);

	this._context.linkProgram(this._program);
//	console.log(this._context.getProgramParameter(this._program, this._context.LINK_STATUS));
//	console.log(this._context.getProgramInfoLog(this._program));
	this._context.useProgram(this._program);
	this._context.enable(this._context.BLEND);

	// look up where the vertex data needs to go.
	this._positionLocationAttr = this._context.getAttribLocation(this._program, "a_position");
	this._texCoordLocationAttr = this._context.getAttribLocation(this._program, "a_texCoord");

	// lookup uniforms
	this._resolutionLocationAttr = this._context.getUniformLocation(this._program, "u_resolution");

	var location = this._context.getUniformLocation(this._program, "u_canvas1");
	this._context.uniform1i(location, 0);

	// Specifies the active texture unit.
	// We use only one texture each time (for each layers draw)
	this._context.activeTexture(this._context.TEXTURE0);//TEXTURE0 to gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS -1
};

/**
 * Create and upload texture's data to memory
 * @param {ImageData|HTMLImageElement|HTMLCanvasElement|HTMLVideoElement|DDSMipmapLevel} data
 * @return {WebGLTexture}
 * @private
 */
WebGLScene.prototype._createTexture = function _createTexture(data) {
	// Create a texture object that will contain the image.
	var texture = this._context.createTexture();

	// Bind the texture the target (TEXTURE_2D) of the active texture unit.
	var target = this._context.TEXTURE_2D;
	// Mimap levels are not required, use only the base (level=0)
	var mipmapLevel = 0;
	var border = 0;
	this._context.bindTexture(target, texture);

	// https://www.khronos.org/registry/webgl/specs/1.0/#5.14.8
	// If image's data or drawable HTML element like img, canvas or video element
	if(
		data instanceof ImageData
		|| data instanceof HTMLImageElement
		|| data instanceof HTMLCanvasElement
		|| data instanceof HTMLVideoElement
	){
		this._context.texImage2D(target, mipmapLevel, this._context.RGBA, this._context.RGBA, this._context.UNSIGNED_BYTE, data);
	}
	/*
	// Bytes of uncompressed image
	else if(data instanceof ArrayBuffer){
		this._context.texImage2D(target, mipmapLevel, this._context.RGBA, width, height, border, this._context.RGBA, this._context.UNSIGNED_BYTE, new Uint8Array(data, 0, data.byteLength));
	}*/
	// A Mipmap level from a DDS file
	else if(data instanceof DDSMipmapLevel){
		var internalFormat;
		switch(data.format){
			case DDSReader.RGB_S3TC_DXT1:
				internalFormat = this._s3tcExtension.COMPRESSED_RGB_S3TC_DXT1_EXT;
				break;
			case DDSReader.RGBA_S3TC_DXT1:
				internalFormat = this._s3tcExtension.COMPRESSED_RGBA_S3TC_DXT1_EXT;
				break;
			case DDSReader.RGBA_S3TC_DXT3:
				internalFormat = this._s3tcExtension.COMPRESSED_RGBA_S3TC_DXT3_EXT;
				break;
			case DDSReader.RGBA_S3TC_DXT5:
				internalFormat = this._s3tcExtension.COMPRESSED_RGBA_S3TC_DXT5_EXT;
				break;
			default:
				throw new TypeError("Unsupported format");
		}
		this._context.compressedTexImage2D(target, mipmapLevel, internalFormat, data.width, data.height, border, new Uint8Array(data.data, 0, data.data.byteLength));
	}
	// Unsupported (yet) data format
	else{
		throw new TypeError("Unsupported data");
	}

	// https://developer.mozilla.org/en-US/docs/Web/WebGL/Using_textures_in_WebGL#Non_power-of-two_textures
	this._context.texParameteri(target, this._context.TEXTURE_MAG_FILTER, this._context.LINEAR);
	this._context.texParameteri(target, this._context.TEXTURE_MIN_FILTER, /*mipmaps > 1 ? this._context.LINEAR_MIPMAP_LINEAR : */this._context.LINEAR);
	this._context.texParameteri(target, this._context.TEXTURE_WRAP_S, this._context.CLAMP_TO_EDGE);//Prevents s-coordinate wrapping (repeating).
	this._context.texParameteri(target, this._context.TEXTURE_WRAP_T, this._context.CLAMP_TO_EDGE);//Prevents t-coordinate wrapping (repeating).

	return texture;
};

WebGLScene.prototype.load = function load() {
	this._tarLoader = new XMLHttpRequest();
	this._tarLoader.addEventListener("progress", this._tarProgressHandlerBound);
	this._tarLoader.addEventListener("error", this._tarErrorHandlerBound);
	this._tarLoader.addEventListener("abort", this._tarAbortHandlerBound);
	this._tarLoader.addEventListener("load", this._tarLoadHandlerBound);
	this._tarLoader.open("GET", this._tarURI, true);
	this._tarLoader.responseType = "arraybuffer";
	this._tarLoader.send(null);
};

WebGLScene.prototype.draw = function draw(positionX, positionY) {
	if(!this._loaded){
		return;
	}

	var i;
	var layer;
	var numLayers = this._layers.length;
	var texCoords;
	// Store texture coords for all layers (for current 2D progress)
	var mergedTexCords = [];
	var rectCoords;
	// Store rect coords for all layers (for current 2D progress)
	var mergedRectCoords = [];
	var numVertices = new Array(numLayers);
	// Store texture for all layers (for current 2D progress)
	var texturesByLayerIndex = new Array(numLayers);
	var width = this._element.width;
	var height = this._element.height;
	// Get vertices and UV texture coordinates
	for(i = 0; i < numLayers; i++){
		layer = this._layers[i];
		texCoords = layer.getTexCoords(positionX, positionY);
		rectCoords = layer.getRectCoords(positionX, positionY, width, height);
		if(texCoords.length != rectCoords.length){
			throw new Error("Num vertices and num UV don't match!");
		}
		numVertices[i] = texCoords.length / 2;//x, y
		mergedTexCords = mergedTexCords.concat(texCoords);
		mergedRectCoords = mergedRectCoords.concat(rectCoords);
		texturesByLayerIndex[i] = layer.getTexture(positionX, positionY);
	}

	// Provide vertices
	this._context.bindBuffer(this._context.ARRAY_BUFFER, this._context.createBuffer());
	this._context.bufferData(this._context.ARRAY_BUFFER, new Float32Array(mergedRectCoords), this._context.STATIC_DRAW);
	this._context.enableVertexAttribArray(this._positionLocationAttr);
	this._context.vertexAttribPointer(this._positionLocationAttr, 2, this._context.FLOAT, false, 0, 0);

	// Provide texture coordinates
	this._context.bindBuffer(this._context.ARRAY_BUFFER, this._context.createBuffer());
	this._context.bufferData(this._context.ARRAY_BUFFER, new Float32Array(mergedTexCords), this._context.STATIC_DRAW);
	this._context.enableVertexAttribArray(this._texCoordLocationAttr);
	this._context.vertexAttribPointer(this._texCoordLocationAttr, 2, this._context.FLOAT, false, 0, 0);

	var drawnVertices = 0;
	var numLayerVertices;
	for(i = 0; i < numLayers; i++) {
		layer = this._layers[i];
		numLayerVertices = numVertices[i];

		// If it's a background layer (should be the first layer)
		if(layer instanceof BackgroundLayer) {
			var color = layer.getColor(positionX, positionY);
			this._context.clearColor(color.r / 255, color.g / 255, color.b / 255, 1.0);
			this._context.clear(this._context.COLOR_BUFFER_BIT);
			this._context.uniform1f(this._context.getUniformLocation(this._program, "u_contrast"), color.contrast / 100);
			this._context.uniform1f(this._context.getUniformLocation(this._program, "u_brightness"), color.brightness / 100);

		} else {
			this._context.uniform1f(this._context.getUniformLocation(this._program, "u_contrast"), 0);
			this._context.uniform1f(this._context.getUniformLocation(this._program, "u_brightness"), 0);
		}

		// Skip layer without texture (for current 2D progress)
		if(!texturesByLayerIndex[i]){
			continue;
		}

		//Set blending function
		if(layer.blending.separate)
			this._context.blendFuncSeparate(layer.blending.srcRGB, layer.blending.dstRGB, layer.blending.srcAlpha, layer.blending.dstAlpha);
		else
			this._context.blendFunc(layer.blending.sFactor, layer.blending.dFactor);

		this._context.bindTexture(this._context.TEXTURE_2D, texturesByLayerIndex[i]);
		// Draw triangles
		this._context.drawArrays(this._context.TRIANGLES, drawnVertices, numLayerVertices);
		drawnVertices += numLayerVertices;
	}
};

/**
 * Test the capability
 * @param {HTMLCanvasElement} element
 * @param {Object} [contextOptions]
 * @returns {boolean}
 */
WebGLScene.test = function test(element, contextOptions){
	var matchType;
	var context;
	contextOptions = contextOptions || {alpha: false};
	// We need a clone to test getContext() on it
	element = element.cloneNode(false);

	try{
		matchType = element instanceof HTMLCanvasElement;
	}catch(error){
		return false;
	}

	if(!matchType){
		return false;
	}

	// Try WebGL
	try{
		context = element.getContext("webgl", contextOptions) || element.getContext("experimental-webgl", contextOptions);
	}catch(error){
		return false;
	}

	// WebGLRenderingContext is available, do more tests
	if(
		context
		// S3TC support
		&& (
			context.getExtension("WEBGL_compressed_texture_s3tc")
			|| context.getExtension("MOZ_WEBGL_compressed_texture_s3tc")
			|| context.getExtension("WEBKIT_WEBGL_compressed_texture_s3tc")
		)
		// Max texture size support
		&& context.getParameter(context.MAX_TEXTURE_SIZE) >= 2048
	){
		// WebGL can be used
		return true;
	}

	return false;
};

WebGLScene.prototype.clear = function clear(){
	Scene.prototype.clear.call(this);

	clearTimeout(this._tarLoadAsyncID);

	if(this._tarLoader){
		this._tarLoader.removeEventListener("progress", this._tarProgressHandlerBound);
		this._tarLoader.removeEventListener("error", this._tarErrorHandlerBound);
		this._tarLoader.removeEventListener("abort", this._tarAbortHandlerBound);
		this._tarLoader.removeEventListener("load", this._tarLoadHandlerBound);
		this._tarLoader.abort();
	}

	for(var src in this._imagesFileBySrc){
		if(this._imagesFileBySrc.hasOwnProperty(src) && this._imagesFileBySrc[src] instanceof Image){
			this._imagesFileBySrc[src].removeEventListener("load", this._imagesLoadHandlerBound, false);
			this._imagesFileBySrc[src] = null;
		}
	}

	var numTextureUnits = this._context.getParameter(this._context.MAX_TEXTURE_IMAGE_UNITS);
	for (var unit = 0; unit < numTextureUnits; ++unit) {
		this._context.activeTexture(this._context.TEXTURE0 + unit);
		this._context.bindTexture(this._context.TEXTURE_2D, null);
		this._context.bindTexture(this._context.TEXTURE_CUBE_MAP, null);
	}
	this._context.bindBuffer(this._context.ARRAY_BUFFER, null);
	this._context.bindBuffer(this._context.ELEMENT_ARRAY_BUFFER, null);
	this._context.bindRenderbuffer(this._context.RENDERBUFFER, null);
	this._context.bindFramebuffer(this._context.FRAMEBUFFER, null);
	// TODO clean canvas context for textures buffers, etc.
	// http://stackoverflow.com/a/23606581/470117


	return true;
};